import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import ModalComponent from '@/components/molecules/ModalComponent/ModalComponent';
import type { PenRef } from '@/domain/providerConfig';
import { parseDate, toDateString } from '@/utils/date';

import { HISTORY_CHUNK_PAUSE_MS, type HistoryRun, useHistoryRun } from '../hooks/useHistoryRun';

import { addDays, daysInclusive, isDay, todayUtc } from './fetchHistoryChunks';

import styles from './modals.module.scss';

export const HISTORY_DEFAULT_DAYS = 90;
export const HISTORY_MAX_DAYS = 400;
/** The server refuses an earlier start (fetch-history.service.ts). */
const EARLIEST_DAY = '2000-01-01';

export interface FetchHistoryModalProps {
    pen: PenRef;
    locationId: number;
    onClose: () => void;
    /** Between two requests; the server's per-user cooldown sets the default. */
    pauseMs?: number;
}

interface WindowErrors {
    from: string | null;
    to: string | null;
}

/** The server's window rules, checked before the first request (from>to at `from`). */
function windowErrors(from: string, to: string, today: string): WindowErrors {
    if (!isDay(from) || from < EARLIEST_DAY || (isDay(to) && from > to))
        return { from: 'providerConfig.errors.invalidWindow', to: null };
    if (!isDay(to) || to > today) return { from: null, to: 'providerConfig.errors.invalidWindow' };
    if (daysInclusive(from, to) > HISTORY_MAX_DAYS)
        return { from: null, to: 'providerConfig.history.tooLong' };
    return { from: null, to: null };
}

function RunStatus({ state }: { state: HistoryRun }) {
    const { t } = useTranslation();
    const { current } = state;
    if (state.phase === 'running' && current !== null)
        return t('providerConfig.history.progress', {
            from: current.from,
            to: current.to,
            done: state.done + 1,
            total: state.total,
        });
    if (state.phase === 'failed' && current !== null)
        return (
            <span className={styles.error}>
                {t('providerConfig.history.failed', {
                    from: current.from,
                    to: current.to,
                    message: t(state.failureKey ?? 'providerConfig.errors.generic'),
                })}
            </span>
        );
    if (state.phase !== 'done') return null;
    return (
        <>
            {state.providerName === null
                ? t('providerConfig.history.nothingWritten')
                : t('providerConfig.history.done', {
                      written: state.written,
                      provider: t(`providerConfig.providers.${state.providerName}`),
                  })}
            {state.failedDays > 0 && (
                <span className={styles.error}>
                    {t('providerConfig.history.failedDays', { count: state.failedDays })}
                </span>
            )}
        </>
    );
}

/** OptoScale admins: fetches a pen's history now, in chunks the server accepts. */
function FetchHistoryModal({
    pen,
    locationId,
    onClose,
    pauseMs = HISTORY_CHUNK_PAUSE_MS,
}: FetchHistoryModalProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const [today] = useState(() => todayUtc());
    const [from, setFrom] = useState(() => addDays(today, 1 - HISTORY_DEFAULT_DAYS));
    const [to, setTo] = useState(today);
    const { state, start } = useHistoryRun(locationId, pauseMs);
    const errors = windowErrors(from, to, today);
    const running = state.phase === 'running';
    const canRun = !running && errors.from === null && errors.to === null;

    function dateField(name: 'from' | 'to', value: string, onChange: (next: string) => void) {
        const error = errors[name];
        return (
            <FormField
                className={styles.dateField}
                label={t(`providerConfig.history.${name}`)}
                htmlFor={`${id}-${name}`}
                error={error === null ? null : t(error)}>
                <StyledSingleDatePicker
                    date={parseDate(value)}
                    maxDate={parseDate(today)}
                    // The picker hands back local noon; the window is that calendar day.
                    onDateChange={(day) => onChange(day === null ? '' : toDateString(day))}
                />
            </FormField>
        );
    }

    return (
        <ModalComponent
            isOpen
            onClose={onClose}
            backdrop={running ? 'static' : true}
            title={t('providerConfig.history.title', { pen: pen.name })}
            body={
                <div className={styles.body}>
                    <p className={styles.text}>{t('providerConfig.history.note')}</p>
                    {/* The picker has no disabled state of its own; a disabled fieldset turns
                        off both dates the native way while the run goes. */}
                    <fieldset className={styles.dates} disabled={running}>
                        {dateField('from', from, setFrom)}
                        {dateField('to', to, setTo)}
                    </fieldset>
                    {/* Always mounted: a live region added together with its text is not announced. */}
                    <p className={styles.status} aria-live="polite">
                        <RunStatus state={state} />
                    </p>
                </div>
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>
                        {t('providerConfig.history.close')}
                    </Button>
                    <Button
                        variant="primary"
                        disabled={!canRun}
                        onClick={() => void start(pen.cageId, { from, to })}>
                        {t('providerConfig.history.run')}
                    </Button>
                </>
            }
        />
    );
}

export default FetchHistoryModal;
export { FetchHistoryModal };
